/**
 * Resource Manager - Complete UI for Resource governance type
 * 
 * Manages external system dependencies and integrations
 * Part of the 15 fundamental workflow types system
 */

import { gql, useMutation, useQuery } from '@ferp/graphql-client';
import {
    ActionIcon,
    Alert,
    Badge,
    Button,
    Card,
    Center,
    Group,
    JsonInput,
    Menu,
    Modal,
    NumberInput,
    Progress,
    RingProgress,
    Select,
    Stack,
    Tabs,
    Text,
    TextInput,
    ThemeIcon,
    Title,
    Tooltip
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
    IconActivity,
    IconAlertTriangle,
    IconCheck,
    IconCloud,
    IconCopy,
    IconDatabase,
    IconDots,
    IconGauge,
    IconPencil,
    IconPlus,
    IconRefresh,
    IconServer,
    IconSettings,
    IconSpeedboat,
    IconTrash,
    IconWifi,
    IconWifiOff,
} from '@tabler/icons-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { Resource } from '../types-16-complete';
import { useGenerateId } from '../hooks/useGenerateId';
import { EntityType } from '../types/EntityType';

interface ResourceManagerProps {
  tenant: string;
  onResourceChange?: (resources: Resource[]) => void;
  readonly?: boolean;
}

interface ResourceHealth {
  status: 'healthy' | 'degraded' | 'critical' | 'unknown';
  latencyMs: number;
  successRate: number;
  usagePercent: number;
  lastCheck: Date;
}

export const ResourceManager: React.FC<ResourceManagerProps> = ({
  tenant,
  onResourceChange,
  readonly = false,
}) => {
  const [resources, setResources] = useState<Resource[]>([]);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [editingResource, setEditingResource] = useState<Partial<Resource> | null>(null);
  const [loading, setLoading] = useState(false);
  const [healthData, setHealthData] = useState<Record<string, ResourceHealth>>({});
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [formErrors, setFormErrors] = useState<Record<string, string | undefined>>({});

  // FERP 5-layer enforcement: Backend ID generation only
  const { generateId } = useGenerateId();

  // Sample resources for development
  const sampleResources: Resource[] = useMemo(() => [
    {
      id: 'res-001',
      name: 'Patient Database (Primary)',
      resourceType: 'database',
      status: 'available',
      endpoint: 'postgresql://localhost:5432/patients',
      limits: {
        rateLimit: 1000,
        concurrentConnections: 50,
        quota: { dailyQueries: 100000, maxResultSize: '10MB' },
      },
      metadata: {
        environment: 'production',
        region: 'us-east-1',
        vendor: 'PostgreSQL',
        version: '14.2',
        backupStrategy: 'daily',
        compliance: ['HIPAA', 'SOC2'],
      },
    },
    {
      id: 'res-002',
      name: 'Claims Processing API',
      resourceType: 'api',
      status: 'available',
      endpoint: 'https://api.claims.claimclock.net/v2',
      limits: {
        rateLimit: 500,
        concurrentConnections: 25,
        quota: { monthlyRequests: 1000000 },
      },
      metadata: {
        authentication: 'oauth2',
        timeoutMs: 30000,
        retryAttempts: 3,
        circuitBreaker: true,
      },
    },
    {
      id: 'res-003',
      name: 'Document Storage S3',
      resourceType: 'storage',
      status: 'degraded',
      endpoint: 's3://ferp-documents-prod',
      limits: {
        quota: { storageGb: 10000, monthlyOperations: 5000000 },
      },
      metadata: {
        encryption: 'AES-256',
        versioning: true,
        lifecyclePolicy: 'archive_after_90_days',
        region: 'us-west-2',
      },
    },
    {
      id: 'res-004',
      name: 'Notification Service',
      resourceType: 'service',
      status: 'available',
      endpoint: 'https://notifications.ferp.internal/api',
      limits: {
        rateLimit: 100,
        quota: { dailyNotifications: 50000 },
      },
      metadata: {
        channels: ['email', 'sms', 'push'],
        priorityLevels: 3,
        retryPolicy: 'exponential_backoff',
      },
    },
  ], []);

  // Sample health data
  const sampleHealthData: Record<string, ResourceHealth> = useMemo(() => ({
    'res-001': {
      status: 'healthy',
      latencyMs: 15,
      successRate: 99.8,
      usagePercent: 45,
      lastCheck: new Date(),
    },
    'res-002': {
      status: 'healthy',
      latencyMs: 120,
      successRate: 99.5,
      usagePercent: 67,
      lastCheck: new Date(),
    },
    'res-003': {
      status: 'degraded',
      latencyMs: 1200,
      successRate: 95.2,
      usagePercent: 85,
      lastCheck: new Date(),
    },
    'res-004': {
      status: 'healthy',
      latencyMs: 85,
      successRate: 98.9,
      usagePercent: 32,
      lastCheck: new Date(),
    },
  }), []);

  // GraphQL wiring
  const GET_RESOURCES = gql`
    query GetResources($tenant: String!) {
      resources(tenant: $tenant) {
        id
        name
        resourceType
        status
        endpoint
        limits
        metadata
      }
    }
  `;
  const UPSERT_RESOURCE = gql`
    mutation UpsertResource($tenant: String!, $resource: ResourceInput!) {
      upsertResource(tenant: $tenant, resource: $resource) { id }
    }
  `;
  const DELETE_RESOURCE = gql`
    mutation DeleteResource($tenant: String!, $id: ID!) { deleteResource(tenant: $tenant, id: $id) }
  `;
  const CHECK_HEALTH = gql`
    mutation CheckResourceHealth($tenant: String!, $id: ID!) {
      checkResourceHealth(tenant: $tenant, id: $id) {
        status
        latencyMs
        successRate
        usagePercent
        lastCheck
      }
    }
  `;

  const { data, refetch } = useQuery<Record<string, any>>(GET_RESOURCES, { variables: { tenant } } as any);
  const [mutateUpsert] = useMutation<Record<string, any>>(UPSERT_RESOURCE);
  const [mutateDelete] = useMutation<Record<string, any>>(DELETE_RESOURCE);
  const [mutateCheck] = useMutation<Record<string, any>>(CHECK_HEALTH);

  useEffect(() => {
    const source = data?.resources || sampleResources;
    setResources(source as any);
    setHealthData(sampleHealthData);
  }, [data, sampleResources, sampleHealthData]);

  const createNewResource = useCallback(() => {
    const newResource: Partial<Resource> = {
      name: '',
      resourceType: 'api',
      status: 'available',
      endpoint: '',
      limits: {
        rateLimit: 100,
        concurrentConnections: 10,
      },
      metadata: {},
    };
    setEditingResource(newResource);
    setSelectedResource(null);
    openModal();
  }, [openModal]);

  const editResource = useCallback((resource: Resource) => {
    setEditingResource({ ...resource });
    setSelectedResource(resource);
    openModal();
  }, [openModal]);

  const saveResource = useCallback(async () => {
    if (!editingResource || !editingResource.name) return;
    
    setLoading(true);
    try {
      const errs: string[] = [];
      if (!editingResource.name) errs.push('Name is required');
      if (!editingResource.resourceType) errs.push('Type is required');
      if (!editingResource.endpoint) errs.push('Endpoint is required');
      if (errs.length > 0) {
        setFormErrors({
          name: !editingResource.name ? 'Required' : undefined,
          type: !editingResource.resourceType ? 'Required' : undefined,
          endpoint: !editingResource.endpoint ? 'Required' : undefined,
        });
        notifications.show({ title: 'Validation error', message: errs.join('; '), color: 'red' });
        return;
      }

      // FERP architecture: Backend ID generation (5-layer enforcement)
      const resourceId = editingResource.id || await generateId(EntityType.resource);

      const resourceToSave: Resource = {
        id: resourceId,
        name: editingResource.name,
        resourceType: editingResource.resourceType || 'api',
        status: editingResource.status || 'available',
        endpoint: editingResource.endpoint,
        limits: editingResource.limits || {},
        metadata: editingResource.metadata || {},
      };

      const updatedResources = selectedResource
        ? resources.map(r => r.id === selectedResource.id ? resourceToSave : r)
        : [...resources, resourceToSave];

      await mutateUpsert({ variables: { tenant, resource: resourceToSave } });
      await refetch?.();
      setResources(updatedResources);
      onResourceChange?.(updatedResources);
      
      notifications.show({
        title: 'Success',
        message: `Resource "${resourceToSave.name}" ${selectedResource ? 'updated' : 'created'} successfully`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      
      closeModal();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to save resource',
        color: 'red',
        icon: <IconAlertTriangle size={16} />,
      });
    } finally {
      setLoading(false);
    }
  }, [editingResource, selectedResource, resources, onResourceChange, closeModal]);

  const deleteResource = useCallback(async (resourceId: string) => {
    setLoading(true);
    try {
      await mutateDelete({ variables: { tenant, id: resourceId } });
      await refetch?.();
      const updatedResources = resources.filter(r => r.id !== resourceId);
      setResources(updatedResources);
      onResourceChange?.(updatedResources);
      const newHealthData = { ...healthData };
      delete newHealthData[resourceId];
      setHealthData(newHealthData);
      notifications.show({ title: 'Resource Deleted', message: 'Resource has been removed', color: 'orange' });
    } finally {
      setLoading(false);
    }
  }, [resources, healthData, onResourceChange, mutateDelete, tenant, refetch]);

  const checkResourceHealth = useCallback(async (resource: Resource) => {
    setLoading(true);
    try {
      const res = await mutateCheck({ variables: { tenant, id: resource.id } });
      const h = res?.data?.checkResourceHealth as any;
      if (h) {
        const normalized: ResourceHealth = {
          status: h.status,
          latencyMs: h.latencyMs,
          successRate: h.successRate,
          usagePercent: h.usagePercent,
          lastCheck: new Date(h.lastCheck),
        };
        setHealthData(prev => ({ ...prev, [resource.id]: normalized }));
        notifications.show({
          title: 'Health Check Complete',
          message: `${resource.name} is ${normalized.status}`,
          color: normalized.status === 'healthy' ? 'green' : 'orange',
          icon: normalized.status === 'healthy' ? <IconCheck size={16} /> : <IconAlertTriangle size={16} />,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [mutateCheck, tenant]);

  const getResourceIcon = (type: Resource['resourceType']) => {
    const icons = {
      api: IconCloud,
      database: IconDatabase,
      storage: IconServer,
      service: IconActivity,
    };
    return icons[type] || IconServer;
  };

  const getStatusColor = (status: Resource['status']) => {
    const colors = {
      available: 'green',
      degraded: 'orange',
      unavailable: 'red',
    };
    return colors[status] || 'gray';
  };

  const getHealthStatusColor = (status: ResourceHealth['status']) => {
    const colors = {
      healthy: 'green',
      degraded: 'orange',
      critical: 'red',
      unknown: 'gray',
    };
    return colors[status] || 'gray';
  };

  return (
    <Stack gap="md">
      <Group justify="apart">
        <div>
          <Title order={3}>
            <Group gap="xs">
              <IconServer size={24} />
              Resource Management
            </Group>
          </Title>
          <Text size="sm" color="dimmed">
            External system dependencies and integrations ({resources.length} resources)
          </Text>
        </div>
        {!readonly && (
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={createNewResource}
            variant="filled"
          >
            New Resource
          </Button>
        )}
      </Group>

      {resources.length === 0 ? (
        <Alert icon={<IconServer size={16} />} title="No Resources Configured">
          <Text size="sm">
            No resources have been configured yet. Click &quot;New Resource&quot; to register your first external system dependency.
          </Text>
        </Alert>
      ) : (
        <Stack gap="sm">
          {resources.map((resource) => {
            const ResourceIcon = getResourceIcon(resource.resourceType);
            const health = healthData[resource.id];
            
            return (
              <Card key={resource.id} shadow="sm" padding="md" withBorder>
                <Group justify="apart" ta="left">
                  <div style={{ flex: 1 }}>
                    <Group gap="xs" ta="center">
                      <ThemeIcon size="sm" variant="light" color="blue">
                        <ResourceIcon size={16} />
                      </ThemeIcon>
                      <Text fw={600}>{resource.name}</Text>
                      <Badge 
                        color={getStatusColor(resource.status)}
                        variant="light"
                        size="sm"
                        leftSection={resource.status === 'available' ? <IconWifi size={12} /> : <IconWifiOff size={12} />}
                      >
                        {resource.status.toUpperCase()}
                      </Badge>
                      <Badge 
                        color="blue"
                        variant="outline"
                        size="xs"
                      >
                        {resource.resourceType}
                      </Badge>
                    </Group>
                    
                    <Text size="sm" color="dimmed" mt="xs" style={{ fontFamily: 'monospace' }}>
                      {resource.endpoint}
                    </Text>
                    
                    <Group gap="lg" mt="xs">
                      {resource.limits.rateLimit && (
                        <Group gap="xs">
                          <IconSpeedboat size={14} color="gray" />
                          <Text size="xs" color="dimmed">
                            {resource.limits.rateLimit}/min
                          </Text>
                        </Group>
                      )}
                      {resource.limits.concurrentConnections && (
                        <Group gap="xs">
                          <IconActivity size={14} color="gray" />
                          <Text size="xs" color="dimmed">
                            {resource.limits.concurrentConnections} conn
                          </Text>
                        </Group>
                      )}
                      {resource.metadata?.environment && (
                        <Badge color="gray" variant="outline" size="xs">
                          {resource.metadata.environment}
                        </Badge>
                      )}
                    </Group>
                    
                    {health && (
                      <Group gap="lg" mt="sm">
                        <Tooltip label={`${health.successRate.toFixed(1)}% success rate`}>
                          <RingProgress
                            size={40}
                            thickness={4}
                            sections={[{ 
                              value: health.successRate, 
                              color: getHealthStatusColor(health.status) 
                            }]}
                            label={
                              <Center>
                                <Text size="xs" fw={700}>
                                  {health.successRate.toFixed(0)}%
                                </Text>
                              </Center>
                            }
                          />
                        </Tooltip>
                        
                        <div>
                          <Text size="xs" color="dimmed">Latency</Text>
                          <Text size="sm" fw={500}>
                            {health.latencyMs}ms
                          </Text>
                        </div>
                        
                        <div>
                          <Text size="xs" color="dimmed">Usage</Text>
                          <Progress
                            value={health.usagePercent}
                            size="sm"
                            color={health.usagePercent > 80 ? 'red' : health.usagePercent > 60 ? 'orange' : 'green'}
                          />
                          <Text size="xs" color="dimmed">
                            {health.usagePercent}%
                          </Text>
                        </div>
                        
                        <div>
                          <Text size="xs" color="dimmed">Status</Text>
                          <Badge 
                            color={getHealthStatusColor(health.status)}
                            variant="filled"
                            size="xs"
                          >
                            {health.status}
                          </Badge>
                        </div>
                      </Group>
                    )}
                  </div>
                  
                  {!readonly && (
                    <Menu shadow="md" width={200}>
                      <Menu.Target>
                        <ActionIcon variant="subtle">
                          <IconDots size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      
                      <Menu.Dropdown>
                        <Menu.Item 
                          leftSection={<IconPencil size={14} />}
                          onClick={() => editResource(resource)}
                        >
                          Edit Resource
                        </Menu.Item>
                        <Menu.Item 
                          leftSection={<IconRefresh size={14} />}
                          onClick={() => checkResourceHealth(resource)}
                          disabled={loading}
                        >
                          Health Check
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconCopy size={14} />}
                          onClick={async () => {
                            // FERP architecture: Backend ID generation (5-layer enforcement)
                            const duplicateId = await generateId(EntityType.resource);
                            const duplicate = {
                              ...resource,
                              id: duplicateId,
                              name: `${resource.name} (Copy)`
                            };
                            setResources([...resources, duplicate]);
                          }}
                        >
                          Duplicate
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item 
                          leftSection={<IconTrash size={14} />}
                          color="red"
                          onClick={() => deleteResource(resource.id)}
                        >
                          Delete
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  )}
                </Group>
              </Card>
            );
          })}
        </Stack>
      )}

      <Modal
        opened={modalOpened}
        onClose={closeModal}
        title={selectedResource ? 'Edit Resource' : 'Create New Resource'}
        size="lg"
        overlayProps={{ opacity: 0.55, blur: 3 }}
      >
        {editingResource && (
          <Tabs defaultValue="basic" variant="outline">
            <Tabs.List>
              <Tabs.Tab value="basic" leftSection={<IconSettings size={16} />}>
                Basic Info
              </Tabs.Tab>
              <Tabs.Tab value="limits" leftSection={<IconGauge size={16} />}>
                Limits & Quotas
              </Tabs.Tab>
              <Tabs.Tab value="metadata" leftSection={<IconDatabase size={16} />}>
                Metadata
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="basic" pt="md">
              <Stack gap="md">
                <TextInput
                  label="Resource Name"
                  placeholder="Enter resource name"
                  required
                  value={editingResource.name || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingResource({ ...editingResource, name: e.target.value })}
                  error={formErrors['name']}
                />
                
                <Group grow>
                  <Select
                    label="Resource Type"
                    placeholder="Select type"
                    required
                    value={editingResource.resourceType}
                    onChange={(value: string | null) => setEditingResource({
                      ...editingResource,
                      resourceType: value as Resource['resourceType']
                    })}
                    data={[
                      { value: 'api', label: 'API Service' },
                      { value: 'database', label: 'Database' },
                      { value: 'storage', label: 'Storage System' },
                      { value: 'service', label: 'External Service' },
                    ]}
                    error={formErrors['type']}
                  />
                  
                  <Select
                    label="Status"
                    placeholder="Select status"
                    required
                    value={editingResource.status}
                    onChange={(value: string | null) => setEditingResource({ 
                      ...editingResource, 
                      status: value as Resource['status'] 
                    })}
                    data={[
                      { value: 'available', label: 'Available' },
                      { value: 'degraded', label: 'Degraded' },
                      { value: 'unavailable', label: 'Unavailable' },
                    ]}
                  />
                </Group>
                
                <TextInput
                  label="Endpoint"
                  placeholder="https://api.example.com or postgresql://host:port/db"
                  value={editingResource.endpoint || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingResource({ ...editingResource, endpoint: e.target.value })}
                  description="Connection string, URL, or identifier for the resource"
                  error={formErrors['endpoint']}
                />
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="limits" pt="md">
              <Stack gap="md">
                <Group grow>
                  <NumberInput
                    label="Rate Limit (per minute)"
                    placeholder="100"
                    value={editingResource.limits?.rateLimit}
                    onChange={(value: number | string | null) => setEditingResource({ 
                      ...editingResource, 
                      limits: { ...editingResource.limits, rateLimit: (typeof value === 'number' ? value : undefined) }
                    })}
                    min={0}
                  />
                  
                  <NumberInput
                    label="Max Concurrent Connections"
                    placeholder="10"
                    value={editingResource.limits?.concurrentConnections}
                    onChange={(value: number | string | null) => setEditingResource({ 
                      ...editingResource, 
                      limits: { ...editingResource.limits, concurrentConnections: (typeof value === 'number' ? value : undefined) }
                    })}
                    min={0}
                  />
                </Group>
                
                <JsonInput
                  label="Quota Limits"
                  placeholder='{"dailyRequests": 10000, "monthlyStorageGb": 1000}'
                  value={JSON.stringify(editingResource.limits?.quota || {}, null, 2)}
                  onChange={(value) => {
                    try {
                      setEditingResource({ 
                        ...editingResource, 
                        limits: { 
                          ...editingResource.limits, 
                          quota: JSON.parse(value) 
                        }
                      });
                    } catch (_e) {
                      // Invalid JSON, ignore
                    }
                  }}
                  minRows={3}
                  description="JSON object defining various quota limits"
                />
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="metadata" pt="md">
              <Stack gap="md">
                <Alert icon={<IconDatabase size={16} />} color="blue">
                  Add environment, authentication, compliance, and other metadata information.
                </Alert>
                
                <JsonInput
                  label="Resource Metadata"
                  placeholder={`{
  "environment": "production",
  "region": "us-east-1",
  "authentication": "oauth2",
  "compliance": ["HIPAA", "SOC2"],
  "timeoutMs": 30000
}`}
                  value={JSON.stringify(editingResource.metadata || {}, null, 2)}
                  onChange={(value) => {
                    try {
                      setEditingResource({ ...editingResource, metadata: JSON.parse(value) });
                    } catch (_e) {
                      // Invalid JSON, ignore
                    }
                  }}
                  minRows={8}
                />
              </Stack>
            </Tabs.Panel>
          </Tabs>
        )}
        
        <Group justify="apart" mt="md">
          <Button variant="light" onClick={closeModal}>
            Cancel
          </Button>
          
          <Button
            onClick={saveResource}
            loading={loading}
            leftSection={<IconCheck size={16} />}
          >
            {selectedResource ? 'Update' : 'Create'} Resource
          </Button>
        </Group>
      </Modal>
    </Stack>
  );
};

export default ResourceManager;